'use client';

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { NextIntlClientProvider, useLocale, useMessages } from 'next-intl';

type RouteIntl = {
  locale: string;
  messages: ReturnType<typeof useMessages>;
} | null;

// Why: docs/app-router-migration/03-i18n-native-nextintl.md#shell-rendered-modals
const RouteIntlContext = createContext<{ current: RouteIntl }>({
  current: null,
});

/** Holds the active route's messages. Mount above every shell modal host. */
export function RouteIntlProvider({ children }: { children: ReactNode }) {
  const ref = useRef<RouteIntl>(null);
  return (
    <RouteIntlContext.Provider value={ref}>
      {children}
    </RouteIntlContext.Provider>
  );
}

/** Publishes the messages visible at its position. Mount inside PageIntl. */
export function RouteIntlPublisher() {
  const ref = useContext(RouteIntlContext);
  const locale = useLocale();
  const messages = useMessages();

  useEffect(() => {
    ref.current = { locale, messages };
  }, [ref, locale, messages]);

  return null;
}

/** Re-provides the route's messages around content a shell host renders. */
export function RouteIntlScope({ children }: { children: ReactNode }) {
  const route = useContext(RouteIntlContext).current;
  if (!route) return <>{children}</>;

  return (
    <NextIntlClientProvider locale={route.locale} messages={route.messages}>
      {children}
    </NextIntlClientProvider>
  );
}
